import { ToastManager } from "utilities/toast/toast-manager";
import { useAtom } from "jotai";
import { useCallback, useState } from "react";
import ShareSpacePanelAtom from "utilities/atoms/bookview/share-space-panel-atom";
import ShareSpaceService from "utilities/services/share-space/share-space-service";
import { t } from "utilities/localization-utils";
import ShareSpaceItemService from "utilities/services/share-space/share-space-items-service";
import SectionService from "utilities/services/sections/section-service";
import PublicationService from "utilities/services/publications/publication-service";
import PublishStatus from "models/enumerations/publish-status";
import ShareSpacePanelRecord from "models/view-models/share-space-panel-record";
import { CollectionUtils } from "utilities/collection-utils";

const useBuildShareSpacePanel = () => {
    const [shareSpacePanel, setShareSpacePanel] = useAtom(ShareSpacePanelAtom);
    const [loading, setLoading] = useState(false);
    const { list: listSections } = SectionService.useBatchList();

    const loadShareSpace = useCallback(async () => {
        setLoading(true);
        const previousId = shareSpacePanel.shareSpace?.id;
        const previousEmails = shareSpacePanel.emails;
        const previousMessage = shareSpacePanel.message;

        try {
            // Create (or find) the share space for this user
            const response = await ShareSpaceService.create();

            // TODO: Error handling
            if (response.result?.hasErrors()) {
                throw response.result.errors;
            }

            const shareSpaceResult = response.resultObject;

            // Get Items
            const shareSpaceItemsResponse = await ShareSpaceItemService.list({
                shareSpaceId: shareSpaceResult?.id!,
            });

            if (shareSpaceItemsResponse.result?.hasErrors()) {
                throw shareSpaceItemsResponse.result.errors;
            }

            const sectionIds = shareSpaceItemsResponse.resultObjects
                .filter((item) => item.sectionId != null)
                .map((item) => item.sectionId!);

            // Get Sections
            const sectionsResponse = CollectionUtils.hasValues(sectionIds)
                ? await listSections(
                      { sectionIds },
                      "sectionIds",
                      sectionIds.length
                  )
                : { resultObjects: [] };

            const publicationIds = [...sectionsResponse.resultObjects].map(
                (caapsItem) => caapsItem.publicationId!
            );

            // Get Publications
            const publicationResponse = CollectionUtils.hasValues(
                publicationIds
            )
                ? await PublicationService.list({ publicationIds })
                : { resultObjects: [] };

            const shareSpaceItemsWithSections =
                shareSpaceItemsResponse.resultObjects
                    .map((item) => {
                        return item.with({
                            section: sectionsResponse.resultObjects.find(
                                (section) =>
                                    section.externalId === item.externalId
                            ),
                            publication: publicationResponse.resultObjects.find(
                                (pub) =>
                                    pub.code === item.code &&
                                    pub.edition === item.edition &&
                                    pub.publishStatus ===
                                        PublishStatus.Published
                            ),
                        });
                    })
                    .sort((a, b) => a.displaySequence! - b.displaySequence!);

            // Populate the context
            setShareSpacePanel(
                new ShareSpacePanelRecord({
                    shareSpace: shareSpaceResult!.with({
                        shareSpaceItems: shareSpaceItemsWithSections ?? [],
                        shareSpaceRecipients:
                            shareSpaceResult?.shareSpaceRecipients ?? [],
                    }),
                    emails:
                        previousId === shareSpaceResult!.id
                            ? previousEmails
                            : [],
                    message:
                        previousId === shareSpaceResult!.id
                            ? previousMessage
                            : "",
                })
            );
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("loading"),
                    resource: t("sharePanel"),
                })
            );
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        setShareSpacePanel,
        shareSpacePanel.emails,
        shareSpacePanel.shareSpace?.id,
    ]);

    return { loading, loadShareSpace };
};

export default useBuildShareSpacePanel;
